import StoryblokClient, { ISbStoryData } from 'storyblok-js-client';
import { unflatten } from './utils/unflatten';
import { SortableItem, sortTree } from './utils/sort-tree';
import { StoryblokService } from '../storyblok';
import { Domain } from '../../types/domain';
import { UserType } from '../../types/user-type';
import { CountryEnum } from '../../types/country';
import { LocaleEnum } from '../../types/locale';
import { GlobalContent } from '../../types/global-content';

interface StoryblokNode {
  children?: SortableItem[];
  id: number;
  is_folder: boolean;
  is_startpage: boolean;
  name: string;
  parent_id: number;
  position: number;
  published: boolean;
  real_path: string;
  slug: string;
  uuid: string;
  page?: PageData;
}

export interface PageData extends ISbStoryData {
  lang: string;
  tag_list: string[];
}

export interface StoryblokNodeTree extends StoryblokNode {
  children: StoryblokNode[];
}

const MANDATORY_NAVIGATION_KEYS = [
  // item attributes
  'id',
  'uuid',
  'real_path',
  'parent_id',
  'is_folder',
  'is_startpage',
  'children',
  'page',
  // page attributes
  'name',
  'content',
  'href',
  'label',
];

const MANDATORY_CONTENT_KEYS = ['navigation_title', 'navigation_subline', 'hide_in_navigation'];

const IGNORE_KEYS = ['highlights', 'page', 'content'];

const attachStoryToLeaf =
  (stories: ISbStoryData[], lang: string = 'default') =>
  (leaf: StoryblokNodeTree): StoryblokNodeTree => {
    const page = stories.find((story) => story.uuid === leaf.uuid);
    return {
      ...leaf,
      real_path: `/${lang !== 'default' ? lang : ''}${leaf.real_path}`.replace('//', '/'),
      children: leaf.children.map(attachStoryToLeaf(stories, lang)),
      page,
    };
  };

const pruneHiddenBranches = (leaf: StoryblokNodeTree): StoryblokNodeTree => ({
  ...leaf,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  page: leaf.page?.tag_list?.length && NavigationService.shouldHide(leaf.page.tag_list) ? undefined : leaf.page,
  children: leaf.children.map(pruneHiddenBranches),
});

const cleanObject = (data, keys = MANDATORY_NAVIGATION_KEYS) => {
  // Remove unnecessary info
  if (!(data instanceof Array)) {
    Object.keys(data)
      .filter((key) => keys.indexOf(key) < 0)
      .forEach((key) => {
        // eslint-disable-next-line no-param-reassign
        delete data[key];
      });
  }

  // Parse content to remove unnecessary info
  if (data.page && typeof data.page.content !== 'undefined') {
    // eslint-disable-next-line no-param-reassign
    data.page.content = cleanObject(data.page.content, MANDATORY_CONTENT_KEYS);
  }

  Object.keys(data)
    .filter((key) => IGNORE_KEYS.indexOf(key) < 0)
    .forEach((prop) => {
      if (data[prop] && typeof data[prop] === 'object') {
        cleanObject(data[prop]);
      }
    });

  return data;
};

export const NavigationService = {
  navigationExclusionTags: ['access:private', 'navigation:hide'],
  navigationForcedInclusionTags: ['navigation:force-show'],
  navigationContactTag: ['navigation:contact-page'],
  storyblokClient: new StoryblokClient({
    accessToken: StoryblokService.getConfig().options.accessToken as string,
  }),

  shouldHide(taglist: string[]): boolean {
    return (
      taglist.some((tag) => this.navigationExclusionTags.indexOf(tag) >= 0 || this.navigationContactTag.indexOf(tag) >= 0) &&
      !taglist.some((tag) => this.navigationForcedInclusionTags.indexOf(tag) >= 0)
    );
  },

  async getAllLinks(): Promise<StoryblokNode[]> {
    const allLinks = await this.storyblokClient.getAll('cdn/links', {
      version: process.env.GATSBY_ENV === 'live' ? 'published' : 'draft',
    });
    return allLinks;
  },

  async getDomainLinks(sbResourceFolders: string[] = []) {
    const allLinks = await this.getAllLinks();
    return allLinks.filter((link: { slug: string }) => sbResourceFolders.find((sbFolder) => link.slug.startsWith(sbFolder)));
  },

  async getSortedTree(sbResourceFolders?: string[]): Promise<StoryblokNodeTree[]> {
    const allLinks = sbResourceFolders ? await this.getDomainLinks(sbResourceFolders) : await this.getAllLinks();
    const tree = unflatten(allLinks) as StoryblokNodeTree[];
    sortTree(tree);
    return tree;
  },

  async getNavigation({
    stories,
    lang,
    sbResourceFolders,
  }: {
    stories: ISbStoryData[];
    lang?: string;
    sbResourceFolders?: string[];
  }): Promise<StoryblokNodeTree[]> {
    const sortedTree = await this.getSortedTree(sbResourceFolders);
    const navigationTree = sortedTree.map(attachStoryToLeaf(stories, lang)).map(pruneHiddenBranches);
    return cleanObject(JSON.parse(JSON.stringify(navigationTree)));
  },

  // todo: refactor with reduce
  async getNavigationForDimensions({
    domain,
    countryLocalesMap,
    globalDatasourceEntries,
    stories,
    sbResourceFolders,
  }: {
    domain: Domain;
    countryLocalesMap: Record<CountryEnum, LocaleEnum[]>;
    globalDatasourceEntries: Record<string, GlobalContent>;
    stories: ISbStoryData[];
    sbResourceFolders?: string[];
  }): Promise<Record<string, Record<UserType, unknown>>> {
    const navigationTree = await this.getNavigation({ stories, sbResourceFolders });

    const countryLocaleBasedNavigation = {};

    Object.entries(countryLocalesMap).forEach(([country, locales]: [string, LocaleEnum[]]) => {
      locales.forEach((locale) => {
        const countryLocaleCode = `${country}-${locale}`;
        const localisedSlug = globalDatasourceEntries[countryLocaleCode].slug;

        countryLocaleBasedNavigation[countryLocaleCode] = this.buildNavigationItemsMap({
          tree: navigationTree,
          locale,
          countryLocaleCode,
          domain,
          localisedSlug,
        });
      });
    });

    return countryLocaleBasedNavigation;
  },

  async getContactPage(lang: string): Promise<ISbStoryData> {
    const queryOptions = {
      with_tag: this.navigationContactTag[0],
      ...(lang !== 'default' && { starts_with: `${lang}/*` }),
    };
    const { data } = await this.storyblokClient.get('cdn/stories/', queryOptions);
    const contactPage = data.stories[0];
    if (contactPage) {
      contactPage.full_slug = `/${contactPage?.full_slug}`.replace('//', '/');
    }
    return contactPage;
  },

  /**
   * Renders the navigation tree for a leaf node.
   * @param leaf The StoryblokNodeTree leaf.
   * @returns An array of navigation entries.
   */
  renderTree(leaf: StoryblokNodeTree): unknown {
    return leaf.children.reduce((acc: any[], e: StoryblokNodeTree) => {
      if (e.is_folder) {
        const entry = {
          text: e.name,
          href: e.real_path,
          children: [] as any[],
        };
        e.children.forEach((c) => {
          if (!c.page?.content?.hide_in_navigation) {
            entry.children.push({
              text: c.page?.content?.navigation_title || c.name,
              href: c.real_path,
            });
          }
        });
        acc.push(entry);
      }
      return acc;
    }, []);
  },

  /**
   * Finds the children nodes for the insti domain using the country locale code.
   * @param tree The navigation tree.
   * @param countryLocaleCode The country locale code.
   * @returns An array of child nodes.
   */
  getChildrenNodesForInsti(tree: StoryblokNodeTree[], countryLocaleCode: string): StoryblokNode[] {
    const found = tree.find((t) => {
      const key = t.slug ? t.slug : t.name.toLowerCase().replace(' - ', '-');
      return key === countryLocaleCode;
    });
    return found ? found.children : [];
  },

  /**
   * Finds the children nodes for the retail domain using the current domain and language.
   * @param tree The navigation tree.
   * @param currentDomain The current domain.
   * @param currentLanguage The current language.
   * @returns An array of child nodes.
   */
  getChildrenNodesForRetail(tree: StoryblokNodeTree[], currentDomain: Domain, currentLanguage: string): StoryblokNode[] {
    const found = tree.find((t) => {
      const key = t.slug ? t.slug : t.name;
      return key === currentDomain;
    });
    const allLanguagesChildren = found ? found.children : [];
    return allLanguagesChildren.filter((c) => c.name === currentLanguage);
  },

  /**
   * Builds the navigation items map.
   * @param tree The navigation tree.
   * @param domain Target domain.
   * @param locale Target language.
   * @param countryLocaleCode The country locale code.
   * @param localisedSlug An object with navigation slugs per user type.
   * @returns A mapping of navigation items keyed by user type.
   */
  buildNavigationItemsMap({
    tree,
    domain,
    locale,
    countryLocaleCode,
    localisedSlug,
  }: {
    tree: StoryblokNodeTree[];
    domain: Domain;
    locale: string;
    countryLocaleCode: string;
    localisedSlug: { retail: string; institutional: string };
  }): Record<UserType, unknown> {
    const isInsti = domain === Domain.INSTI;

    const targetChildrenStoryblokNodes = isInsti
      ? this.getChildrenNodesForInsti(tree, countryLocaleCode)
      : this.getChildrenNodesForRetail(tree, domain, locale);

    return targetChildrenStoryblokNodes.reduce(
      (acc: Record<UserType, unknown>, leaf: StoryblokNode) => {
        const storyblokNodeTree = leaf as StoryblokNodeTree;
        const userType = isInsti ? StoryblokService.getUserTypeFromSlug(storyblokNodeTree.real_path, localisedSlug) : UserType.RETAIL;

        if (!userType) {
          return acc;
        }

        return { ...acc, [userType]: this.renderTree(storyblokNodeTree) };
      },
      {} as Record<UserType, unknown>,
    );
  },
};
